/**
 * The app's transient-confirmation channel (BON-472).
 *
 * There was no toast anywhere in the app before this, and no dependency provides
 * one — `ShiftRequestsPanel` and the users page both carry comments wishing for
 * it. So this is a shared primitive from the start rather than a bookings-page
 * one-off.
 *
 * WHAT BELONGS HERE: a confirmation the CSR can walk away from — "the link is on
 * its way". WHAT DOES NOT: anything they must act on. Those take a dialog, which
 * takes focus and cannot be missed; a toast that carries a required action is a
 * toast that gets dismissed unread.
 */
import { createContext, useContext } from 'react';

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastRequest {
  message: string;
  /** Default `success` — the only tone the criteria actually ask for. */
  tone?: ToastTone;
  /** Auto-dismiss delay. Defaults to {@link DEFAULT_TOAST_MS}. */
  durationMs?: number;
}

export interface Toast extends ToastRequest {
  id: number;
}

export interface ToastApi {
  showToast: (toast: ToastRequest) => void;
  dismissToast: (id: number) => void;
}

/** Long enough to read a sentence, short enough not to sit over the table. */
export const DEFAULT_TOAST_MS = 5000;

/**
 * Without a provider a toast is DROPPED, not thrown.
 *
 * A missing provider must not white-screen a CSR mid-call over a confirmation
 * message, so this degrades to a logged no-op. The warning is the signal — it
 * fires in the test that renders the surface, which is where it gets caught.
 */
const FALLBACK: ToastApi = {
  showToast: ({ message }) =>
    console.warn(`[toast] no ToastProvider mounted — dropped: ${message}`),
  dismissToast: () => {},
};

export const ToastContext = createContext<ToastApi>(FALLBACK);

export function useToast(): ToastApi {
  return useContext(ToastContext);
}
